import { Platform, StyleSheet, Text, type TextProps } from "react-native";

import { useThemeColor } from "@/hooks/useThemeColor";
import { Fonts, ThemeColor } from "@/constants/ThemeColours";

export type ThemedTextProps = TextProps & {
  lightColor?: string;
  darkColor?: string;
  type?:
    | "default"
    | "title"
    | "editorialTitle"
    | 'small'
    | 'smallBold'
    | "defaultSemiBold"
    | "subtitle"
    | 'link'
    | 'linkPrimary'
    | 'code'
    | "stamp"
    | "fieldLabel";
  themeColor?: ThemeColor;
};

export function ThemedText({ style, lightColor, darkColor, type = "default", themeColor, ...rest }: ThemedTextProps) {
  const color = useThemeColor({ light: lightColor, dark: darkColor }, themeColor ?? "text");

  return (
    <Text
      style={[
        { color },
        type === "default" ? styles.default : undefined,
        type === "title" ? styles.title : undefined,
        type === "editorialTitle" ? styles.editorialTitle : undefined,
        type === 'small' && styles.small,
        type === 'smallBold' && styles.smallBold,
        type === "defaultSemiBold" ? styles.defaultSemiBold : undefined,
        type === "subtitle" ? styles.subtitle : undefined,
        type === 'link' && styles.link,
        type === 'linkPrimary' && styles.linkPrimary,
        type === 'code' && styles.code,
        type === "stamp" && styles.stamp,
        type === "fieldLabel" && styles.fieldLabel,
        style,
      ]}
      {...rest}
    />
  );
}

const styles = StyleSheet.create({
  default: {
    fontFamily: Fonts.sans,
    fontSize: 15,
    lineHeight: 22,
  },
  small: {
    fontFamily: Fonts.sans,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "500",
  },
  smallBold: {
    fontFamily: Fonts.sans,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
  },
  defaultSemiBold: {
    fontFamily: Fonts.sans,
    fontSize: 15,
    lineHeight: 22,
    fontWeight: "600",
  },
  title: {
    fontFamily: Fonts.sans,
    fontSize: 28,
    fontWeight: "bold",
    lineHeight: 30,
  },
  editorialTitle: {
    fontFamily: Fonts.serif,
    fontSize: 24,
    fontWeight: "700",
    letterSpacing: -0.2,
    lineHeight: 30,
  },
  stamp: {
    fontFamily: Fonts.sans,
    fontSize: 12,
    fontWeight: "600",
    letterSpacing: 0.5,
    lineHeight: 16,
    textTransform: "uppercase",
  },
  subtitle: {
    fontFamily: Fonts.sans,
    fontSize: 18,
    fontWeight: "bold",
  },
  link: {
    fontFamily: Fonts.sans,
    lineHeight: 26,
    fontSize: 14,
  },
  linkPrimary: {
    fontFamily: Fonts.sans,
    lineHeight: 26,
    fontSize: 14,
    color: '#3c87f7',
  },
  code: {
    fontFamily: Fonts.mono,
    fontWeight: Platform.select({ android: 700 }) ?? 500,
    fontSize: 12,
  },
  fieldLabel: {
    fontFamily: Fonts.sans,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "600",
    marginBottom: 2,
    opacity: 0.75,
    letterSpacing: 0.2,
  },
});
